import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { BitmapText, Container } from '@pixi/react';
import { useMemo } from 'react';
import { isMobile } from 'react-device-detect';
import { shallowEqual } from 'react-redux';
import { useAppSelector } from '../../../redux/store';
import { BET_POT_TEXT_STROKE_COLOR_NUM } from '../../../styles/colors';
import { getChipsDisplayStr } from '../../../utils/chipsDisplayUtils';
import { withBitmapTextMetrics } from '../../../utils/textMetricsUtils';
import { isTestEnvironment } from '../../../utils/utils';
import { Rect } from '../pixiComponents/rect';
const RECT_PADDING_WIDTH_PX = 2;
const RECT_PADDING_HEIGHT_PX = -2;
const RECT_X_ADJUSTMENT_PX = -1;
const RECT_Y_ADJUSTMENT_PX = -2;
const LETTER_SPACING = 1;
function getAmountBitmapTextStyle(isSeatedPlayerIn, currency) {
    switch (currency) {
        case 'chips': {
            return isSeatedPlayerIn ? 'active-player-chips' : 'inactive-player-chips-or-status';
        }
        case 'jacks': {
            return isSeatedPlayerIn ? 'active-player-jacks' : 'inactive-player-jacks';
        }
    }
}
function getStatusAndStyle(seatedPlayerNumChips, isSeatedPlayerSittingOut, isSeatedPlayerStandingUp, isSeatedPlayerIn, currency, showChipsAsBbMultiple, bigBlind) {
    if (seatedPlayerNumChips === undefined ||
        isSeatedPlayerSittingOut === undefined ||
        isSeatedPlayerStandingUp === undefined ||
        isSeatedPlayerIn === undefined ||
        !currency) {
        return;
    }
    const styleOptions = { letterSpacing: LETTER_SPACING };
    if (isSeatedPlayerSittingOut) {
        return withBitmapTextMetrics({
            text: 'SitOut',
            bitmapTextStyleType: 'inactive-player-chips-or-status'
        }, styleOptions);
    }
    if (isSeatedPlayerStandingUp) {
        return withBitmapTextMetrics({
            text: 'Stand',
            bitmapTextStyleType: 'inactive-player-chips-or-status'
        }, styleOptions);
    }
    if (!seatedPlayerNumChips) {
        return withBitmapTextMetrics({
            text: 'ALL-IN',
            bitmapTextStyleType: 'all-in'
        }, styleOptions);
    }
    // show chips/jacks amount
    return withBitmapTextMetrics({
        text: getChipsDisplayStr({ numChips: seatedPlayerNumChips, showChipsAsBbMultiple, bigBlind }),
        bitmapTextStyleType: getAmountBitmapTextStyle(isSeatedPlayerIn, currency)
    });
}
export const PlayerChipsOrStatusDisplay = ({ seatIndex }) => {
    const seatedPlayerNumChips = useAppSelector((state) => state.poker.seatedPlayers[seatIndex]?.numChips);
    const isSeatedPlayerSittingOut = useAppSelector((state) => state.poker.seatedPlayers[seatIndex]?.isSittingOut);
    const isSeatedPlayerStandingUp = useAppSelector((state) => state.poker.seatedPlayers[seatIndex]?.isStandingUp);
    const isSeatedPlayerIn = useAppSelector((state) => state.poker.seatedPlayers[seatIndex]?.isIn);
    const chipsOrStatusTopRight = useAppSelector((state) => state.gameDimensions.chipsOrStatusTopRight, shallowEqual);
    const currency = useAppSelector((state) => state.poker.currency);
    const bigBlind = useAppSelector((state) => state.poker.bigBlind);
    const showChipsAsBbMultiple = useAppSelector((state) => state.selfData.isShowChipsAsBbMultipleEnabled);
    const statusAndStyle = useMemo(() => getStatusAndStyle(seatedPlayerNumChips, isSeatedPlayerSittingOut, isSeatedPlayerStandingUp, isSeatedPlayerIn, currency, showChipsAsBbMultiple, bigBlind), [
        currency,
        isSeatedPlayerIn,
        isSeatedPlayerSittingOut,
        isSeatedPlayerStandingUp,
        seatedPlayerNumChips,
        showChipsAsBbMultiple,
        bigBlind
    ]);
    if (!statusAndStyle || !chipsOrStatusTopRight) {
        return null;
    }
    const { text, bitmapTextStyleType, dimensions } = statusAndStyle;
    // NOTE this pixi data should be set in all environments as it is used to know how to move pots to player stacks:
    const pixiData = { type: 'player-chips-or-status-container', seatIndex };
    return (_jsxs(Container, { "pixi-data": pixiData, interactiveChildren: false, x: chipsOrStatusTopRight.x, y: chipsOrStatusTopRight.y, children: [isMobile && (_jsx(Rect, { x: -dimensions.w - RECT_PADDING_WIDTH_PX + RECT_X_ADJUSTMENT_PX, y: -RECT_PADDING_HEIGHT_PX + RECT_Y_ADJUSTMENT_PX, w: Math.max(0, dimensions.w + 2 * RECT_PADDING_WIDTH_PX), h: Math.max(0, dimensions.h + 2 * RECT_PADDING_HEIGHT_PX), fillColor: BET_POT_TEXT_STROKE_COLOR_NUM, radius: 10, fillAlpha: 0.7 })), _jsx(BitmapText
            // There appears to be a react-pixi-fiber bug, where, without this changing `key`,
            // react-pixi-fiber fails to re-render BitmapText when style.fontName changes:
            , { "pixi-data": isTestEnvironment() ? { type: 'player-chips-or-status', seatIndex, currency } : null, text: text, style: { fontName: bitmapTextStyleType, letterSpacing: LETTER_SPACING }, roundPixels: true, anchor: [1, 0] }, `${bitmapTextStyleType} ${text}`)] }));
};
